// 🔗 MÓDULO DE INTEGRACIÓN DEFONTANA - ENTORNO DE VALIDACIÓN
// Este módulo replica los endpoints reales del ERP Defontana con estructura y respuestas equivalentes
// para entorno de validación técnica antes del despliegue productivo

import { useState } from 'react';
import { supabase, testSupabaseConnection, safeSupabaseQuery } from './supabaseClient';
import { getGastosFromBackend } from './backendApi';

// URL del backend Node.js + Express (intermediario con Mock Server Defontana)
// En desarrollo: http://localhost:3002/api
// En producción: usar variable de entorno REACT_APP_API_URL
const DEFONTANA_VALIDATION_API_URL = process.env.REACT_APP_API_URL 
  || process.env.REACT_APP_DEFONTANA_VALIDATION_API_URL;

// ✅ Validación: No permitir fallback a localhost en producción
if (!DEFONTANA_VALIDATION_API_URL) {
  console.error('❌ [mockApi] REACT_APP_API_URL no configurada');
  throw new Error('REACT_APP_API_URL debe estar configurada en las variables de entorno');
}

// ✅ Aserción de tipo: después de la validación, siempre estará definida
const API_URL: string = DEFONTANA_VALIDATION_API_URL;

export interface MockApiResponse<T> {
  success: boolean;
  data: T | null;
  message?: string;
  errors?: string[];
}

export interface Gasto {
  id: string;
  fecha: string;
  monto: number;
  descripcion: string;
  categoria: string;
  trabajador: string;
  documento?: string | null;
  sincronizado: boolean;
  fechaSincronizacion?: string;
  aprobado?: boolean;
  monto_ocr?: number; // ✅ NUEVO: Monto detectado por OCR
  estado_alerta?: 'pendiente' | 'aprobado' | 'rechazado'; // ✅ Estado de alerta del gasto
}

export interface Compra {
  // Campos principales desde Defontana (Mock Server)
  numero_factura: string;
  proveedor: string;
  descripcion: string;
  monto: number; // Monto en CLP (pesos chilenos)
  mes: string; // Nombre del mes: "Octubre", "Noviembre", "Diciembre", etc.
  // Campos opcionales
  fecha?: string; // Formato: DD/MM/YYYY o YYYY-MM-DD (se normaliza en el frontend). Opcional porque el Mock Server puede no incluirlo
  id?: string;
  categoria?: string;
  estado?: 'pendiente' | 'aprobado' | 'rechazado';
}

export interface Venta {
  id?: string; // Opcional, puede venir del Mock Server como numero_boleta
  numero_boleta?: string; // Campo del Mock Server
  fecha?: string; // Opcional, puede venir como mes
  mes?: string; // Campo del Mock Server
  cliente: string;
  monto: number;
  categoria?: string; // Opcional
  descripcion?: string; // Campo del Mock Server
  estado?: 'pendiente' | 'completada' | 'cancelada';
  productos?: Array<{ nombre: string; cantidad: number; precio: number }>;
}

export interface ReporteData {
  totalGasto: number;
  promedioDiario: number;
  categoriaMayor: string;
  variacionMesAnterior: number;
  totalMovimientos: number;
  gastoProyectado: number;
  desviacionEstandar: number;
  evolucionGastos: Array<{ mes: string; real: number; proyectado: number }>;
  gastosPorCategoria: Array<{ categoria: string; monto: number; porcentaje: number }>;
}

export interface KPIData {
  totalGastosMes: number;
  promedioPorCategoria: number;
  variacionMensual: number;
  gastosAprobados: number;
  gastosPendientes: number;
  totalCategorias?: number;
  totalGastos?: number;
}

// Manejo de errores HTTP del entorno de validación Defontana
const handleError = (response: Response | any): string => {
  // Si no es un Response, intentar convertir a string
  if (!response) {
    return 'Error desconocido en la API Defontana.';
  }
  
  // Si es un objeto pero no un Response, intentar extraer información
  if (typeof response !== 'object' || !('status' in response)) {
    try {
      if (response.message && typeof response.message === 'string') {
        return response.message;
      }
      if (typeof response === 'string') {
        return response;
      }
      const errorString = JSON.stringify(response);
      return errorString !== '{}' ? `Error: ${errorString}` : 'Error desconocido en la API Defontana.';
    } catch (e) {
      return String(response || 'Error desconocido en la API Defontana.');
    }
  }
  
  // Si es un Response válido
  if (response.status === 404) {
    return 'Endpoint no encontrado en la API Defontana. Verifica la configuración del entorno de validación.';
  } else if (response.status === 500) {
    return 'Error interno del servidor. Intenta nuevamente más tarde.';
  } else if (!response.ok) {
    const statusText = response.statusText || 'Error desconocido';
    return `Error HTTP ${response.status}: ${statusText}`;
  }
  return 'Error desconocido en la API Defontana.';
};

// Función genérica para hacer peticiones HTTP al entorno de validación Defontana
// Replica fielmente la estructura de la API REST Defontana, utilizada en fase de validación técnica
async function makeRequest<T>(
  endpoint: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
  data?: any
): Promise<MockApiResponse<T>> {
  const url = `${API_URL}${endpoint}`;
  
  const config: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
    },
  };

  if (data && (method === 'POST' || method === 'PUT')) {
    config.body = JSON.stringify(data);
  }

  try {
    console.log(`🔗 Defontana API (validación): ${method} ${url}`);
    console.log(`   📍 Backend URL: ${API_URL}`);
    
    const response = await fetch(url, {
      ...config,
      mode: 'cors', // Asegurar que use CORS
      credentials: 'omit' // No enviar credenciales
    });
    
    if (!response.ok) {
      let errorMessage: string;
      try {
        errorMessage = handleError(response);
      } catch (e) {
        // Si handleError falla, usar mensaje genérico
        errorMessage = `Error HTTP ${response.status}: ${response.statusText || 'Error desconocido'}`;
        console.error('Error al procesar handleError:', e);
      }
      
      // Asegurar que errorMessage sea siempre un string
      if (typeof errorMessage !== 'string') {
        errorMessage = `Error HTTP ${response.status}: ${response.statusText || 'Error desconocido'}`;
      }
      
      console.error(`❌ Error Defontana API (${response.status}):`, errorMessage);
      
      return {
        success: false,
        data: null,
        message: errorMessage,
        errors: [errorMessage]
      };
    }

    // Verificar que el Content-Type sea JSON antes de intentar parsear
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const textResponse = await response.text();
      console.error(`❌ Error Defontana API: Respuesta no es JSON`);
      console.error(`   Content-Type recibido: ${contentType || 'no especificado'}`);
      console.error(`   Primeros 200 caracteres de la respuesta:`, textResponse.substring(0, 200));
      console.error(`   URL solicitada: ${url}`);
      
      // Si es HTML, probablemente el backend no está corriendo o la URL es incorrecta
      if (textResponse.includes('<!DOCTYPE') || textResponse.includes('<html')) {
        return {
          success: false,
          data: null as T,
          message: `El backend no está respondiendo correctamente. Verifica que el servidor esté corriendo en ${API_URL}`,
          errors: ['El servidor retornó HTML en lugar de JSON. Verifica que el backend esté corriendo.']
        };
      }
      
      return {
        success: false,
        data: null as T,
        message: `La respuesta del servidor no es JSON válido (Content-Type: ${contentType})`,
        errors: ['La respuesta del servidor no es JSON válido']
      };
    }
    
    let result: any;
    try {
      result = await response.json();
    } catch (jsonError) {
      // Si la respuesta no es JSON válido, retornar error
      const textResponse = await response.text();
      console.error(`❌ Error Defontana API: Error al parsear JSON`, jsonError);
      console.error(`   Primeros 200 caracteres de la respuesta:`, textResponse.substring(0, 200));
      console.error(`   URL solicitada: ${url}`);
      return {
        success: false,
        data: null as T,
        message: 'La respuesta de la API Defontana no es JSON válido',
        errors: ['La respuesta de la API Defontana no es JSON válido']
      };
    }
    
    console.log(`✅ Datos de validación Defontana cargados correctamente.`, result);
    console.log(`   📊 Tipo de resultado: ${Array.isArray(result) ? 'Array' : typeof result}`);
    if (result && typeof result === 'object' && !Array.isArray(result)) {
      console.log(`   🔑 Keys del objeto:`, Object.keys(result));
    }
    
    // Normalizar respuesta del backend
    // El backend puede retornar directamente un array o un objeto con { status/success, message, data }
    // El Mock Server de Defontana retorna: { status: "success", message: "...", data: [...] }
    if (Array.isArray(result)) {
      // Si la respuesta es un array directo (común en GET /compras, /ventas, /gastos)
      console.log(`   ✅ Respuesta es un array con ${result.length} elementos`);
      return {
        success: true,
        data: result as T,
        message: undefined,
        errors: undefined
      };
    } else if (result && typeof result === 'object') {
      // Si ya tiene formato estructurado
      if ('status' in result && result.status === 'success' && 'data' in result) {
        // Formato del Mock Server: { status: "success", message: "...", data: [...] }
        console.log(`   ✅ Formato Mock Server detectado: status="success", data es ${Array.isArray(result.data) ? `array con ${result.data.length} elementos` : typeof result.data}`);
        return {
          success: true,
          data: result.data as T,
          message: result.message,
          errors: undefined
        };
      } else if ('success' in result) {
        // Ya tiene formato { success, data }
        console.log(`   ✅ Formato { success, data } detectado`);
        return {
          success: result.success,
          data: result.data || result,
          message: result.message,
          errors: result.errors
        };
      } else if ('data' in result) {
        // Tiene formato { data, ... }
        console.log(`   ✅ Formato { data, ... } detectado`);
        return {
          success: true,
          data: result.data as T,
          message: result.message,
          errors: result.errors
        };
      } else {
        // Es un objeto simple, tratarlo como data
        console.log(`   ⚠️  Objeto simple sin estructura conocida, tratando como data`);
        return {
          success: true,
          data: result as T,
          message: undefined,
          errors: undefined
        };
      }
    } else {
      // Respuesta primitiva
      console.log(`   ⚠️  Respuesta primitiva: ${typeof result}`);
      return {
        success: true,
        data: result as T,
        message: undefined,
        errors: undefined
      };
    }
  } catch (error) {
    console.error(`❌ Error en integración Defontana:`, error);
    console.error(`   🔗 URL intentada: ${url}`);
    console.error(`   📍 Backend esperado: ${API_URL.replace('/api', '')}`);
    
    // Manejo seguro de errores
    let errorMessage: string;
    if (error instanceof Error) {
      // Detectar errores específicos de conexión
      if (error.message.includes('Failed to fetch') || 
          error.message.includes('NetworkError') ||
          error.message.includes('Network request failed') ||
          error.message.includes('ERR_CONNECTION_REFUSED') ||
          error.message.includes('ERR_NETWORK_CHANGED')) {
        errorMessage = `No se pudo conectar con el backend. Verifica que el servidor esté corriendo en ${API_URL.replace('/api', '')}. Error: ${error.message}`;
        console.error(`   💡 Solución: Ejecuta 'cd server && npm run dev' para iniciar el backend`);
      } else if (error.name === 'AbortError' || error.message.includes('timeout')) {
        errorMessage = 'Timeout: El servidor no respondió a tiempo. Verifica que el backend esté corriendo.';
      } else {
        errorMessage = error.message;
      }
    } else if (typeof error === 'string') {
      errorMessage = error;
    } else if (error && typeof error === 'object') {
      try {
        errorMessage = JSON.stringify(error);
      } catch (e) {
        errorMessage = String(error || 'Error de conexión con la API Defontana');
      }
    } else {
      errorMessage = String(error || 'Error de conexión con la API Defontana');
    }
    
    return {
      success: false,
      data: null as T,
      message: errorMessage,
      errors: [errorMessage]
    };
  }
}

// 📦 ENDPOINT: /gastos
// Obtener lista de gastos desde la PLATAFORMA (NO desde Defontana)
// Los gastos de Caja Chica se registran en la plataforma y se almacenan en Supabase
// NOTA: Esta función NO usa el backend de Defontana, los gastos vienen directamente de la plataforma
export async function getGastos(filters?: {
  fechaInicio?: string;
  fechaFin?: string;
  categoria?: string;
  trabajador?: string;
}): Promise<MockApiResponse<Gasto[]>> {
  
  // ✅ OPCIÓN 1: Intentar primero con backend como puente (evita RLS y timeout)
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  if (BACKEND_URL) {
    try {
      console.log('🔍 [getGastos] Intentando obtener gastos desde backend (puente con Supabase)...');
      const backendResult = await getGastosFromBackend(filters);
      
      if (backendResult.success && Array.isArray(backendResult.data)) {
        console.log(`✅ [getGastos] ${backendResult.data.length} gastos obtenidos desde backend${backendResult.cached ? ' (cache)' : ''}`);
        
        // Mapear datos de Supabase (snake_case) a formato de la aplicación (camelCase)
        const gastosMapeados: Gasto[] = (backendResult.data || []).map((gasto: any) => {
          // ✅ MEJORADO: Convertir monto_ocr de forma robusta (maneja string, number, null, undefined)
          // Nota: Los datos del backend pueden ser string, así que validamos también cadena vacía
          let montoOcr: number | undefined = undefined;
          const montoOcrRaw = gasto.monto_ocr;
          if (montoOcrRaw !== null && montoOcrRaw !== undefined && montoOcrRaw !== '' && String(montoOcrRaw).trim() !== '') {
            const parsed = typeof montoOcrRaw === 'string' 
              ? Number(montoOcrRaw) 
              : Number(montoOcrRaw);
            if (!isNaN(parsed)) {
              montoOcr = parsed;
            }
          }
          
          return {
            id: gasto.id,
            fecha: gasto.fecha,
            monto: Number(gasto.monto),
            descripcion: gasto.descripcion,
            categoria: gasto.categoria,
            trabajador: gasto.trabajador || 'Sin asignar',
            documento: gasto.documento || null,
            sincronizado: gasto.sincronizado === false ? false : true,
            aprobado: gasto.aprobado !== undefined ? gasto.aprobado : true,
            monto_ocr: montoOcr, // ✅ NUEVO: Incluir monto_ocr convertido a número
            estado_alerta: gasto.estado_alerta || null, // ✅ CRÍTICO: Incluir estado_alerta desde la BD
            created_at: gasto.created_at, // ✅ IMPORTANTE: Preservar created_at para auditoría
            updated_at: gasto.updated_at // ✅ IMPORTANTE: Preservar updated_at para auditoría
          };
        });
        
        return {
          success: true,
          data: gastosMapeados,
          message: `Gastos obtenidos desde backend${backendResult.cached ? ' (cache)' : ''}`
        };
      }
    } catch (backendError: any) {
      console.warn('⚠️ [getGastos] Backend falló, intentando Supabase directo:', backendError?.message);
      // Continuar con Supabase directo como fallback
    }
  } else {
    console.log('ℹ️ [getGastos] Backend URL no configurada, usando Supabase directo');
  }
  
  // ✅ FALLBACK: Si backend no está disponible o falla, usar Supabase directo
  try {
    // Verificar que Supabase esté configurado
    if (!supabase) {
      console.error('❌ Cliente de Supabase no disponible');
      return {
        success: false,
        data: [],
        message: 'Cliente de Supabase no disponible. Verifica la configuración.',
        errors: ['Cliente de Supabase no disponible']
      };
    }
    
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
    
    if (!supabaseUrl || !supabaseKey) {
      console.error('❌ Variables de entorno de Supabase no configuradas');
      return {
        success: false,
        data: [],
        message: 'Variables de entorno de Supabase no configuradas. Verifica tu archivo .env',
        errors: ['Variables de entorno no configuradas']
      };
    }
    
    console.log('   ✅ Cliente de Supabase disponible');
    console.log('🔍 [getGastos] Iniciando consulta a tabla gastos (Supabase directo)...');
    console.log('🔍 [getGastos] Filtros aplicados:', filters || 'ninguno');
    
    // ✅ MEJORADO: Verificar sesión antes de consultar
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    console.log('🔍 [getGastos] Verificando sesión del usuario...', {
      tieneSesion: !!sessionData?.session,
      userId: sessionData?.session?.user?.id,
      userEmail: sessionData?.session?.user?.email,
      error: sessionError?.message
    });
    
    if (!sessionData?.session) {
      console.warn('⚠️ [getGastos] No hay sesión activa, intentando consulta de todas formas...');
    }
    
    // Primero intentar una consulta simple para verificar conectividad y permisos
    try {
      console.log('🔍 [getGastos] Verificando acceso a tabla gastos con consulta simple...');
      const testStartTime = Date.now();
      
      const testResult = await safeSupabaseQuery(
        async () => {
          console.log('🔍 [getGastos] Ejecutando consulta de prueba...');
          const result = await supabase
            .from('gastos')
            .select('id')
            .limit(1);
          console.log('🔍 [getGastos] Resultado de consulta de prueba:', {
            tieneData: !!result.data,
            dataLength: result.data?.length || 0,
            tieneError: !!result.error,
            errorCode: result.error?.code,
            errorMessage: result.error?.message
          });
          return result;
        },
        {
          timeout: 15000, // ✅ AUMENTADO: 15 segundos para dar más tiempo
          retries: 1 // ✅ REDUCIDO: Solo 1 reintento para diagnóstico más rápido
        }
      );
      
      const testElapsed = Date.now() - testStartTime;
      console.log(`⏱️ [getGastos] Consulta de prueba completada en ${testElapsed}ms`);
      
      if (testResult?.error) {
        console.error('❌ [getGastos] Error en consulta de prueba:', {
          code: testResult.error.code,
          message: testResult.error.message,
          details: testResult.error.details,
          hint: testResult.error.hint,
          elapsed: testElapsed
        });
        
        // Si es error de RLS, retornar inmediatamente
        if (testResult.error.code === 'PGRST301' || 
            testResult.error.message?.includes('permission') || 
            testResult.error.message?.includes('policy') ||
            testResult.error.message?.includes('RLS')) {
          console.error('⚠️ [getGastos] Error de permisos RLS detectado en consulta de prueba');
          return {
            success: false,
            data: [],
            message: 'Error de permisos al consultar gastos. Verifica las políticas RLS en Supabase para la tabla "gastos".',
            errors: [testResult.error.message]
          };
        }
        
        // Si es timeout, retornar con mensaje específico
        if (testResult.error.code === 'TIMEOUT_ERROR' || testResult.error.message?.includes('timeout')) {
          console.error('❌ [getGastos] Timeout en consulta de prueba - posible problema de RLS o conectividad');
          console.error('💡 [getGastos] Diagnóstico:');
          console.error('   - Tiempo transcurrido:', testElapsed, 'ms');
          console.error('   - Sesión activa:', !!sessionData?.session);
          console.error('   - User ID:', sessionData?.session?.user?.id);
          console.error('   - Verifica en Supabase Dashboard:');
          console.error('     1. Tabla "gastos" existe');
          console.error('     2. Políticas RLS permiten SELECT a usuarios autenticados');
          console.error('     3. El usuario tiene permisos para leer la tabla');
          console.error('     4. Conectividad con Supabase');
          return {
            success: false,
            data: [],
            message: 'La consulta está tardando demasiado. Verifica las políticas RLS de la tabla "gastos" en Supabase y tu conexión a internet.',
            errors: ['Timeout en consulta de prueba a gastos']
          };
        }
      } else {
        console.log('✅ [getGastos] Consulta de prueba exitosa, continuando con consulta completa...');
      }
    } catch (testError: any) {
      console.warn('⚠️ [getGastos] Error inesperado en consulta de prueba, continuando de todas formas:', testError);
    }
    
    // Construir query base
    let query = supabase
      .from('gastos')
      .select('*')
      .order('fecha', { ascending: false });
    
    console.log('🔍 [getGastos] Query construida, ejecutando consulta completa...');
    
    // Aplicar filtros
    if (filters?.fechaInicio) {
      query = query.gte('fecha', filters.fechaInicio);
      console.log('🔍 [getGastos] Filtro fechaInicio aplicado:', filters.fechaInicio);
    }
    if (filters?.fechaFin) {
      query = query.lte('fecha', filters.fechaFin);
      console.log('🔍 [getGastos] Filtro fechaFin aplicado:', filters.fechaFin);
    }
    if (filters?.categoria) {
      query = query.eq('categoria', filters.categoria);
      console.log('🔍 [getGastos] Filtro categoria aplicado:', filters.categoria);
    }
    if (filters?.trabajador) {
      query = query.eq('trabajador', filters.trabajador);
      console.log('🔍 [getGastos] Filtro trabajador aplicado:', filters.trabajador);
    }
    
    // ✅ MEJORADO: Usar safeSupabaseQuery para mejor manejo de timeouts y retries
    const startTime = Date.now();
    let data, error;
    
    try {
      const result = await safeSupabaseQuery(
        async () => {
          const queryResult = await query;
          return queryResult;
        },
        {
          timeout: 20000, // 20 segundos para consulta completa
          retries: 2 // Reintentar 2 veces en caso de timeout
        }
      );
      
      const elapsed = Date.now() - startTime;
      console.log(`⏱️ [getGastos] Consulta completada en ${elapsed}ms`);
      data = result?.data as any;
      error = result?.error;
      
      // 🔍 LOG TEMPORAL: Verificar datos crudos de Supabase
      if (data && Array.isArray(data) && data.length > 0) {
        console.log('🔍 [getGastos] Datos crudos de Supabase (primeros 3):', data.slice(0, 3).map((g: any) => ({
          id: g.id,
          fecha: g.fecha,
          monto: g.monto,
          monto_ocr: g.monto_ocr,
          tiene_monto_ocr: g.monto_ocr !== null && g.monto_ocr !== undefined
        })));
        // Logs removidos para reducir ruido en consola
      }
    } catch (timeoutError: any) {
      const elapsed = Date.now() - startTime;
      if (timeoutError?.message?.includes('Timeout')) {
        console.error(`❌ [getGastos] Timeout al consultar gastos después de ${elapsed}ms - posible problema de RLS o conectividad`);
        console.error('💡 [getGastos] Verifica en Supabase Dashboard:');
        console.error('   1. Tabla "gastos" existe');
        console.error('   2. Políticas RLS permiten SELECT a usuarios autenticados');
        console.error('   3. El usuario tiene permisos para leer la tabla');
        return {
          success: false,
          data: [],
          message: 'La consulta está tardando demasiado. Verifica las políticas RLS de la tabla "gastos" en Supabase.',
          errors: ['Timeout en consulta a gastos']
        };
      }
      throw timeoutError;
    }
    
    if (error) {
      console.error('❌ Error al obtener gastos desde Supabase:', {
        code: error.code,
        message: error.message,
        details: error.details,
        hint: error.hint,
        status: error.status
      });
      
      // Si es un error de RLS, dar mensaje más específico
      if (error.code === 'PGRST301' || error.message?.includes('permission') || error.message?.includes('policy')) {
        console.error('⚠️ [getGastos] Error de permisos RLS - verificar políticas de la tabla gastos');
        return {
          success: false,
          data: [],
          message: 'Error de permisos al consultar gastos. Verifica las políticas RLS en Supabase.',
          errors: [error.message]
        };
      }
      
      return {
        success: false,
        data: [],
        message: `Error al obtener gastos: ${error.message}`,
        errors: [error.message]
      };
    }
    
    // Mapear datos de Supabase (snake_case) a formato de la aplicación (camelCase)
    const dataArray = Array.isArray(data) ? data : [];
    console.log('🔍 [getGastos] Antes de mapear. Total datos:', dataArray.length);
    const gastosMapeados: Gasto[] = dataArray.map((gasto: any) => {
      // ✅ MEJORADO: Convertir monto_ocr de forma robusta (maneja string, number, null, undefined)
      // Nota: Los datos del backend pueden ser string, así que validamos también cadena vacía
      let montoOcr: number | undefined = undefined;
      const montoOcrRaw = gasto.monto_ocr;
      if (montoOcrRaw !== null && montoOcrRaw !== undefined && montoOcrRaw !== '' && String(montoOcrRaw).trim() !== '') {
        const parsed = typeof montoOcrRaw === 'string' 
          ? Number(montoOcrRaw) 
          : Number(montoOcrRaw);
        if (!isNaN(parsed)) {
          montoOcr = parsed;
        }
      }
      
      // Logs removidos para reducir ruido en consola
      
      // ✅ MEJORADO: Convertir documento a string si es un objeto
      let documentoFinal: string | null = null;
      if (gasto.documento) {
        if (typeof gasto.documento === 'string') {
          documentoFinal = gasto.documento.trim() || null;
        } else if (typeof gasto.documento === 'object') {
          // Si es un objeto, intentar extraer el valor de propiedades comunes
          if (gasto.documento.path) {
            documentoFinal = String(gasto.documento.path).trim() || null;
          } else if (gasto.documento.url) {
            documentoFinal = String(gasto.documento.url).trim() || null;
          } else if (gasto.documento.publicUrl) {
            documentoFinal = String(gasto.documento.publicUrl).trim() || null;
          } else {
            // Si no se puede extraer, intentar convertir a JSON string
            console.warn('⚠️ [getGastos] Campo documento es un objeto sin propiedades conocidas:', gasto.documento);
            documentoFinal = null;
          }
        }
      }
      
      return {
        id: gasto.id,
        fecha: gasto.fecha,
        monto: Number(gasto.monto),
        descripcion: gasto.descripcion,
        categoria: gasto.categoria,
        trabajador: gasto.trabajador || 'Sin asignar',
        documento: documentoFinal,
        // Si sincronizado es null/undefined, asumir true (si está en la BD, se guardó exitosamente)
        // Solo si es explícitamente false, mantenerlo como false
        sincronizado: gasto.sincronizado === false ? false : true,
        aprobado: gasto.aprobado !== undefined ? gasto.aprobado : true,
        monto_ocr: montoOcr, // ✅ NUEVO: Incluir monto_ocr convertido a número
        estado_alerta: gasto.estado_alerta || null, // ✅ CRÍTICO: Incluir estado_alerta desde la BD
        created_at: gasto.created_at, // ✅ IMPORTANTE: Preservar created_at para auditoría
        updated_at: gasto.updated_at // ✅ IMPORTANTE: Preservar updated_at para auditoría
      };
    });
    
    // Logs removidos para reducir ruido en consola
    
    // Gastos obtenidos correctamente
    return {
      success: true,
      data: gastosMapeados,
      message: `Gastos obtenidos correctamente (${gastosMapeados.length} registros)`,
      errors: undefined
    };
  } catch (error) {
    console.error('❌ Error al conectar con Supabase:', error);
    // Si hay error de conexión, retornar array vacío en lugar de fallar
    return {
      success: true,
      data: [],
      message: 'No se pudo conectar con Supabase. Verifica la configuración.',
      errors: error instanceof Error ? [error.message] : ['Error desconocido']
    };
  }
}

// Función helper para detectar irregularidades en un nuevo gasto
// Retorna true si hay irregularidades, false si está todo correcto
function detectarIrregularidadesEnGasto(
  nuevoGasto: Omit<Gasto, 'id' | 'sincronizado'>,
  gastosExistentes: Gasto[] = []
): boolean {
  let tieneIrregularidades = false;
  
  // 1. Verificar diferencia entre monto y monto_ocr
  // ✅ MEJORADO: Validación más robusta para monto_ocr
  // monto_ocr es de tipo number | undefined, así que solo verificamos null/undefined
  if (nuevoGasto.monto_ocr !== null && nuevoGasto.monto_ocr !== undefined) {
    // monto_ocr ya es number según la interfaz, no necesitamos convertir
    const montoOcr = nuevoGasto.monto_ocr;
    
    if (!isNaN(montoOcr) && typeof nuevoGasto.monto === 'number') {
      const diferencia = Math.abs(montoOcr - nuevoGasto.monto);
      
      // ✅ NUEVO: Logs de depuración para identificar problemas
      console.log('🔍 [mockApi] Comparando montos:', {
        monto: nuevoGasto.monto,
        monto_ocr: montoOcr,
        diferencia,
        monto_ocr_original: nuevoGasto.monto_ocr,
        tipo_monto_ocr_original: typeof nuevoGasto.monto_ocr
      });
      
      if (diferencia > 100) {
        console.log('⚠️ [mockApi] Irregularidad detectada: Diferencia entre monto y monto_ocr:', diferencia);
        tieneIrregularidades = true;
      } else {
        console.log('✅ [mockApi] Diferencia dentro del umbral permitido (<= 100)');
      }
    } else {
      console.warn('⚠️ [mockApi] No se pudo comparar montos:', {
        monto: nuevoGasto.monto,
        monto_ocr: montoOcr,
        monto_es_numero: typeof nuevoGasto.monto === 'number',
        monto_ocr_es_numero: !isNaN(montoOcr)
      });
    }
  } else {
    console.log('ℹ️ [mockApi] monto_ocr no disponible para comparación:', {
      monto_ocr: nuevoGasto.monto_ocr,
      tipo: typeof nuevoGasto.monto_ocr
    });
  }
  
  // 2. Verificar gastos duplicados (mismo descripción, monto y fecha)
  const duplicado = gastosExistentes.some(g => {
    const mismaDescripcion = g.descripcion?.toLowerCase().trim() === nuevoGasto.descripcion?.toLowerCase().trim();
    const mismoMonto = g.monto === nuevoGasto.monto;
    const mismaFecha = g.fecha === nuevoGasto.fecha;
    return mismaDescripcion && mismoMonto && mismaFecha;
  });
  
  if (duplicado) {
    console.log('⚠️ [mockApi] Irregularidad detectada: Gasto duplicado');
    tieneIrregularidades = true;
  }
  
  // 3. Verificar monto inusual (muy alto - más de 500.000 CLP)
  const UMBRAL_MONTO_INUSUAL = 500000;
  if (typeof nuevoGasto.monto === 'number' && nuevoGasto.monto > UMBRAL_MONTO_INUSUAL) {
    console.log('⚠️ [mockApi] Irregularidad detectada: Monto inusual (muy alto):', nuevoGasto.monto);
    tieneIrregularidades = true;
  }
  
  return tieneIrregularidades;
}

// Crear nuevo gasto en Supabase (plataforma interna)
// Los gastos se guardan en Supabase, NO en Defontana
export async function createGasto(gasto: Omit<Gasto, 'id' | 'sincronizado'>): Promise<MockApiResponse<Gasto>> {
  
  try {
    // Verificar conexión con Supabase
    if (!supabase) {
      console.error('❌ Cliente de Supabase no disponible');
      throw new Error('Cliente de Supabase no disponible. Verifica las variables de entorno REACT_APP_SUPABASE_URL y REACT_APP_SUPABASE_ANON_KEY');
    }
    
    // Verificar que las variables de entorno estén configuradas
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
    
    if (!supabaseUrl || !supabaseKey) {
      console.error('❌ Variables de entorno de Supabase no configuradas');
      console.error('   REACT_APP_SUPABASE_URL:', supabaseUrl ? '✅ Configurada' : '❌ Faltante');
      console.error('   REACT_APP_SUPABASE_ANON_KEY:', supabaseKey ? '✅ Configurada' : '❌ Faltante');
      throw new Error('Variables de entorno de Supabase no configuradas. Verifica tu archivo .env');
    }
    
    // ✅ NUEVO: Obtener gastos existentes para detectar duplicados
    let gastosExistentes: Gasto[] = [];
    try {
      const resultadoGastos = await getGastos();
      if (resultadoGastos.success && resultadoGastos.data) {
        gastosExistentes = resultadoGastos.data;
      }
    } catch (error) {
      console.warn('⚠️ [mockApi] No se pudieron obtener gastos existentes para verificar duplicados:', error);
      // Continuar sin verificar duplicados si falla
    }
    
    // ✅ NUEVO: Detectar irregularidades antes de guardar
    const tieneIrregularidades = detectarIrregularidadesEnGasto(gasto, gastosExistentes);
    
    // ✅ CORREGIDO: Solo aprobar automáticamente si NO hay irregularidades
    const estadoAlertaFinal = tieneIrregularidades ? 'pendiente' : 'aprobado';
    
    console.log('🔍 [mockApi] Estado de alerta determinado:', {
      tieneIrregularidades,
      estadoAlertaFinal,
      monto: gasto.monto,
      monto_ocr: gasto.monto_ocr,
      descripcion: gasto.descripcion
    });
    
    // Mapear datos de la aplicación (camelCase) a formato de Supabase (snake_case)
    // IMPORTANTE: Asegurar que trabajador siempre tenga un valor (no null ni vacío)
    const trabajadorFinal = gasto.trabajador && gasto.trabajador.trim() !== '' 
      ? gasto.trabajador.trim() 
      : null; // Si está vacío, dejar null para que el trigger lo asigne
    
    const gastoParaSupabase = {
      fecha: gasto.fecha,
      monto: gasto.monto,
      descripcion: gasto.descripcion,
      categoria: gasto.categoria,
      trabajador: trabajadorFinal,
      documento: gasto.documento || null, // ✅ Asegurar que documento sea string o null, no undefined
      monto_ocr: gasto.monto_ocr || null, // ✅ NUEVO: Incluir monto_ocr si existe
      sincronizado: true, // Se marca como sincronizado al guardarse exitosamente en la BD
      aprobado: estadoAlertaFinal === 'aprobado', // ✅ CORREGIDO: Solo aprobar si no hay irregularidades
      estado_alerta: estadoAlertaFinal, // ✅ CORREGIDO: 'pendiente' si hay irregularidades, 'aprobado' si no
      usuario_modificacion: trabajadorFinal || 'sistema'
    };
    
    // Log para verificar que el documento se está enviando a Supabase
    console.log('📤 [mockApi] Guardando gasto en Supabase con documento:', {
      documento: gastoParaSupabase.documento,
      tipoDocumento: typeof gastoParaSupabase.documento,
      tieneDocumento: !!gastoParaSupabase.documento
    });
    
    // ✅ CRÍTICO: Agregar timeout para evitar que se quede colgado por RLS
    // Usar el mismo patrón que en loginWithEmail y getGastos
    const TIMEOUT_MS = 10000; // 10 segundos (igual que en createGastoWithOfflineFallback)
    
    const insertQuery = supabase
      .from('gastos')
      .insert([gastoParaSupabase])
      .select()
      .single();
    
    const insertTimeout = new Promise<{ data: null, error: { message: string; code?: string } }>((_, reject) => {
      setTimeout(() => reject(new Error('Timeout al insertar gasto (10s)')), TIMEOUT_MS);
    });
    
    let data, error;
    try {
      const result = await Promise.race([insertQuery, insertTimeout]) as any;
      data = result?.data;
      error = result?.error;
    } catch (timeoutError: any) {
      if (timeoutError?.message?.includes('Timeout')) {
        console.error('❌ [mockApi] Timeout al insertar gasto - posible problema de RLS o conectividad');
        console.error('💡 [mockApi] Verifica las políticas RLS de la tabla "gastos" en Supabase');
        return {
          success: false,
          data: null,
          message: 'La operación tardó demasiado. Verifica las políticas RLS de la tabla "gastos" en Supabase.',
          errors: ['Timeout al insertar gasto']
        };
      }
      throw timeoutError;
    }
    
    if (error) {
      console.error('❌ Error al guardar gasto en Supabase:', error);
      console.error('   📊 Detalles del error:', {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint
      });
      
      // ✅ Manejar errores RLS específicamente
      if (error.code === 'PGRST301' || 
          error.message?.includes('permission') || 
          error.message?.includes('policy') ||
          error.message?.includes('RLS')) {
        console.error('⚠️ [mockApi] Error de permisos RLS detectado');
        console.error('💡 [mockApi] Verifica que exista una política RLS que permita:');
        console.error('   INSERT en gastos para usuarios autenticados');
        return {
          success: false,
          data: null,
          message: 'Error de permisos al guardar gasto. Verifica las políticas RLS en Supabase.',
          errors: [error.message]
        };
      }
      
      return {
        success: false,
        data: null,
        message: `Error al guardar gasto: ${error.message}`,
        errors: [error.message]
      };
    }
    
    if (!data) {
      console.error('❌ No se retornaron datos después de insertar');
      return {
        success: false,
        data: null,
        message: 'No se recibieron datos del servidor después de guardar',
        errors: ['No se recibieron datos del servidor']
      };
    }
    
    // Mapear respuesta de Supabase a formato de la aplicación
    // Si el gasto se guardó exitosamente, sincronizado debe ser true
    
    // ✅ MEJORADO: Convertir documento a string si es un objeto o null
    let documentoFinal: string | null = null;
    if (data.documento) {
      if (typeof data.documento === 'string') {
        documentoFinal = data.documento.trim() || null;
      } else if (typeof data.documento === 'object') {
        const docObj = data.documento as any;
        if (docObj.path) {
          documentoFinal = String(docObj.path).trim() || null;
        } else if (docObj.url) {
          documentoFinal = String(docObj.url).trim() || null;
        } else if (docObj.publicUrl) {
          documentoFinal = String(docObj.publicUrl).trim() || null;
        } else {
          console.warn('⚠️ [mockApi] Campo documento es un objeto sin propiedades conocidas:', data.documento);
          documentoFinal = null;
        }
      }
    }
    
    const gastoCreado: Gasto = {
      id: data.id,
      fecha: data.fecha,
      monto: Number(data.monto),
      descripcion: data.descripcion,
      categoria: data.categoria,
      trabajador: data.trabajador || 'Sin asignar',
      documento: documentoFinal, // ✅ Usar documento convertido
      sincronizado: data.sincronizado !== undefined ? data.sincronizado : true, // Si se guardó exitosamente, está sincronizado
      aprobado: data.aprobado !== undefined ? data.aprobado : true,
      monto_ocr: data.monto_ocr ? Number(data.monto_ocr) : undefined, // ✅ NUEVO: Incluir monto_ocr
      estado_alerta: data.estado_alerta || undefined // ✅ CRÍTICO: Incluir estado_alerta para que aparezca inmediatamente en panel de pendientes
    };
    
    // Log para verificar que el documento se recibió correctamente desde Supabase
    console.log('📥 [mockApi] Gasto creado recibido de Supabase:', {
      id: gastoCreado.id,
      documento: gastoCreado.documento,
      tipoDocumento: typeof gastoCreado.documento,
      tieneDocumento: !!gastoCreado.documento,
      documentoRaw: data.documento,
      documentoOriginal: data.documento,
      documentoConvertido: documentoFinal
    });
    
    // Gasto guardado exitosamente
    
    return {
      success: true,
      data: gastoCreado,
      message: 'Gasto creado exitosamente',
      errors: undefined
    };
  } catch (error) {
    console.error('❌ Error al conectar con Supabase:', error);
    if (error instanceof Error) {
      console.error('   📊 Stack trace:', error.stack);
    }
    return {
      success: false,
      data: null,
      message: `No se pudo conectar con Supabase: ${error instanceof Error ? error.message : 'Error desconocido'}`,
      errors: error instanceof Error ? [error.message] : ['Error desconocido']
    };
  }
}

// Actualizar gasto existente en la API REST Defontana
export async function updateGasto(id: string, gasto: Partial<Gasto>): Promise<MockApiResponse<Gasto>> {
  return makeRequest<Gasto>(`/gastos/${id}`, 'PUT', gasto);
}

// 🔄 FUNCIÓN WRAPPER CON FALLBACK OFFLINE
// Intenta crear el gasto en Supabase, si falla lo guarda offline automáticamente
export async function createGastoWithOfflineFallback(
  gasto: Omit<Gasto, 'id' | 'sincronizado'>,
  imagenFile?: File
): Promise<MockApiResponse<Gasto>> {
  try {
    // Intentar crear el gasto normalmente con timeout de 10 segundos
    const timeoutPromise = new Promise<MockApiResponse<Gasto>>((_, reject) =>
      setTimeout(() => reject(new Error('Timeout: La operación tardó demasiado')), 10000)
    );

    const result = await Promise.race([
      createGasto(gasto),
      timeoutPromise
    ]);

    // Si fue exitoso, retornar el resultado
    if (result.success) {
      return result;
    }

    // Si falló, guardar offline
    throw new Error(result.message || 'Error al guardar gasto');
  } catch (error) {
    // Detectar si es un error de red, timeout, o Supabase caído
    const isNetworkError = 
      error instanceof TypeError && error.message.includes('fetch') ||
      error instanceof Error && (
        error.message.includes('Timeout') ||
        error.message.includes('Failed to fetch') ||
        error.message.includes('NetworkError') ||
        error.message.includes('ERR_CONNECTION_REFUSED') ||
        error.message.includes('ERR_NETWORK_CHANGED') ||
        !navigator.onLine
      );

    // Si es error de red o Supabase caído, guardar offline
    if (isNetworkError || !navigator.onLine) {
      console.log('💾 Guardando gasto offline debido a error de conexión...');
      
      try {
        // Importar dinámicamente para evitar dependencias circulares
        const { addExpenseToQueue } = await import('./offlineQueue');
        const { offlineStore } = await import('./offlineStore');
        
        // Guardar en cola offline
        const idTemp = await addExpenseToQueue(gasto, imagenFile);
        
        // Notificar al store
        offlineStore.addExpenseOffline();
        
        // Retornar respuesta indicando que se guardó offline
        return {
          success: true, // Marcamos como success porque se guardó offline
          data: {
            id: idTemp,
            ...gasto,
            sincronizado: false
          } as Gasto,
          message: 'Gasto guardado sin conexión. Se sincronizará automáticamente cuando se restaure la conexión.',
          errors: undefined
        };
      } catch (offlineError) {
        console.error('❌ Error al guardar offline:', offlineError);
        // Si falla el guardado offline, retornar error
        return {
          success: false,
          data: null,
          message: `Error al guardar gasto: ${error instanceof Error ? error.message : 'Error desconocido'}`,
          errors: [error instanceof Error ? error.message : 'Error desconocido']
        };
      }
    }

    // Si no es error de red, retornar el error original
    return {
      success: false,
      data: null,
      message: error instanceof Error ? error.message : 'Error desconocido',
      errors: error instanceof Error ? [error.message] : ['Error desconocido']
    };
  }
}

// Sincronizar gastos (enviar múltiples gastos a la API REST Defontana)
export async function syncGastos(gastos: Gasto[]): Promise<MockApiResponse<{ synced: number; errors: string[] }>> {
  return makeRequest<{ synced: number; errors: string[] }>('/gastos/sync', 'POST', { gastos });
}

// 📦 ENDPOINT: /compras
// Obtener lista de compras desde Defontana (Mock Server)
// Fuente de datos: Defontana (Mock Server en Postman)
// Integración unidireccional: Solo lectura desde Defontana
export async function getCompras(filters?: {
  fechaInicio?: string;
  fechaFin?: string;
  proveedor?: string;
  estado?: string;
}): Promise<MockApiResponse<Compra[]>> {
  const queryParams = new URLSearchParams();
  
  if (filters?.fechaInicio) queryParams.append('fechaInicio', filters.fechaInicio);
  if (filters?.fechaFin) queryParams.append('fechaFin', filters.fechaFin);
  if (filters?.proveedor) queryParams.append('proveedor', filters.proveedor);
  if (filters?.estado) queryParams.append('estado', filters.estado);

  const endpoint = `/compras${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  return makeRequest<Compra[]>(endpoint);
}

// 📦 ENDPOINT: /ventas
// Obtener lista de ventas desde Defontana (Mock Server)
// Fuente de datos: Defontana (Mock Server en Postman)
// Integración unidireccional: Solo lectura desde Defontana
export async function getVentas(filters?: {
  fechaInicio?: string;
  fechaFin?: string;
  cliente?: string;
  estado?: string;
}): Promise<MockApiResponse<Venta[]>> {
  const queryParams = new URLSearchParams();
  
  if (filters?.fechaInicio) queryParams.append('fechaInicio', filters.fechaInicio);
  if (filters?.fechaFin) queryParams.append('fechaFin', filters.fechaFin);
  if (filters?.cliente) queryParams.append('cliente', filters.cliente);
  if (filters?.estado) queryParams.append('estado', filters.estado);

  const endpoint = `/ventas${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  return makeRequest<Venta[]>(endpoint);
}

// 📊 ENDPOINT: /reportes
// Obtener datos de reportes desde la API REST Defontana (entorno de validación)
// Replica fielmente la estructura de la API REST Defontana, utilizada en fase de validación técnica
export async function getReportes(filters?: {
  fechaInicio?: string;
  fechaFin?: string;
  agrupacion?: 'dia' | 'semana' | 'mes';
}): Promise<MockApiResponse<ReporteData>> {
  const queryParams = new URLSearchParams();
  
  if (filters?.fechaInicio) queryParams.append('fechaInicio', filters.fechaInicio);
  if (filters?.fechaFin) queryParams.append('fechaFin', filters.fechaFin);
  if (filters?.agrupacion) queryParams.append('agrupacion', filters.agrupacion);

  const endpoint = `/reportes${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  return makeRequest<ReporteData>(endpoint);
}

// 📊 ENDPOINT: /reportes/kpi
// Obtener datos de KPIs desde la API REST Defontana (entorno de validación)
// NOTA: Este endpoint puede no estar disponible en el backend unidireccional
// Por ahora, retornamos datos mock hasta que se implemente en el Mock Server
export async function getKPIs(): Promise<MockApiResponse<KPIData>> {
  // El backend unidireccional solo tiene /compras y /ventas
  // Los KPIs se calculan localmente desde los datos obtenidos
  // Por ahora, retornamos datos mock para evitar errores de conexión
  
  console.log('📊 Obteniendo KPIs (usando datos mock por ahora)');
  console.log('   ⚠️  Los KPIs se calcularán localmente desde compras y ventas');
  
  // Retornar datos mock por ahora
  // TODO: Calcular KPIs reales desde compras y ventas obtenidas
  return {
    success: true,
    data: {
      totalGastosMes: 0,
      promedioPorCategoria: 0,
      variacionMensual: 0,
      gastosAprobados: 0,
      gastosPendientes: 0,
      totalGasto: 0,
      promedioDiario: 0,
      categoriaMayor: '',
      variacionMesAnterior: 0,
      totalMovimientos: 0,
      gastoProyectado: 0,
      desviacionEstandar: 0
    } as KPIData,
    message: 'KPIs calculados localmente (datos mock)',
    errors: undefined
  };
  
  // NOTA: Cuando se implemente, usar:
  // const endpoint = '/reportes/kpi';
  // return makeRequest<KPIData>(endpoint);
}

// Hook personalizado para usar la API REST Defontana (entorno de validación)
export const useMockApi = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const executeRequest = async <T>(
    requestFn: () => Promise<MockApiResponse<T>>
  ): Promise<MockApiResponse<T>> => {
    setLoading(true);
    setError(null);

    try {
      const result = await requestFn();
      
      if (!result.success) {
      const errorMsg = result.message || 'Error en la petición a la API Defontana';
      setError(typeof errorMsg === 'string' ? errorMsg : String(errorMsg));
      }
      
      return result;
    } catch (err) {
      // Manejo seguro de errores
      let errorMessage: string;
      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === 'string') {
        errorMessage = err;
      } else if (err && typeof err === 'object') {
        try {
          errorMessage = JSON.stringify(err);
        } catch (e) {
          errorMessage = String(err || 'Error desconocido en la API Defontana');
        }
      } else {
        errorMessage = String(err || 'Error desconocido en la API Defontana');
      }
      
      setError(errorMessage);
      
      return {
        success: false,
        data: null,
        message: errorMessage,
        errors: [errorMessage]
      };
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    executeRequest,
    clearError: () => setError(null)
  };
};

// Utilidad para manejo de errores de la API Defontana
export const handleMockApiError = (error: any): string => {
  // Si el error es un string, retornarlo directamente
  if (typeof error === 'string') {
    return error;
  }
  
  // Si el error es null o undefined
  if (!error) {
    return 'Error desconocido al conectar con la API Defontana.';
  }
  
  // Si el error tiene una propiedad message que es string
  if (error.message && typeof error.message === 'string') {
    // Verificar códigos de error específicos
    if (error.status === 404 || error.message.includes('404')) {
      return 'Endpoint no encontrado en la API Defontana. Verifica la configuración del entorno de validación.';
    } else if (error.status === 500 || error.message.includes('500')) {
      return 'Error interno del servidor. Intenta nuevamente más tarde.';
    } else if (error.code === 'NETWORK_ERROR' || error.message.includes('network') || error.message.includes('Network')) {
      return 'Error de conexión con la API Defontana. Verifica tu conexión a internet.';
    } else if (error.code === 'TIMEOUT' || error.message.includes('timeout') || error.message.includes('Timeout')) {
      return 'Tiempo de espera agotado. Intenta nuevamente.';
    }
    return error.message;
  }
  
  // Si el error tiene una propiedad status
  if (error.status === 404) {
    return 'Endpoint no encontrado en la API Defontana. Verifica la configuración del entorno de validación.';
  } else if (error.status === 500) {
    return 'Error interno del servidor. Intenta nuevamente más tarde.';
  }
  
  // Si el error tiene una propiedad code
  if (error.code === 'NETWORK_ERROR') {
    return 'Error de conexión con la API Defontana. Verifica tu conexión a internet.';
  } else if (error.code === 'TIMEOUT') {
    return 'Tiempo de espera agotado. Intenta nuevamente.';
  }
  
  // Si el error es un objeto, intentar convertirlo a string de forma segura
  try {
    const errorString = JSON.stringify(error);
    if (errorString && errorString !== '{}') {
      return `Error en la API Defontana: ${errorString}`;
    }
  } catch (e) {
    // Si no se puede serializar, usar toString
    if (error.toString && typeof error.toString === 'function') {
      const errorStr = error.toString();
      if (errorStr !== '[object Object]') {
        return errorStr;
      }
    }
  }
  
  // Mensaje por defecto
  return 'Error desconocido al conectar con la API Defontana.';
};

